import { ReactElement, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { User } from '../../../auth/types/user';

import { axiosClient } from './provider';

interface AxiosErrorBoundaryProps {
  children: ReactElement;
}

export const AxiosErrorBoundary: React.FC<AxiosErrorBoundaryProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const response = queryClient.getQueryState<User>(['getAuth']);

  useEffect(() => {
    const responseInterceptor = axiosClient.interceptors.response.use(undefined, (error) => {
      if (error.response && error.response?.status) {
        const statusCode = error.response.status;

        switch (true) {
          case response?.data && statusCode === 401:
            queryClient.invalidateQueries({ queryKey: ['getAuth'] });
            break;
          case statusCode === 403 || statusCode === 404:
            navigate(`${location.pathname}`, { state: { notFound: true } });
            break;
        }
      }

      throw error;
    });

    // To avoid the infinity loop if the latter also receives the response code 401, we eject the interceptor in this case
    return () => {
      axiosClient.interceptors.response.eject(responseInterceptor);
    };
  }, [response?.data]);

  return children;
};
